import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormField,
  PageHeader,
  PageSkeleton,
  inputClass,
} from "@/components/ui";
import { LicenseListEditor, MAX_LICENSES } from "@/components/profile/license-list-editor";
import { CompanyEvidenceCard } from "@/components/profile/company-evidence-card";
import { CompanyLogoCard } from "@/components/profile/company-logo-card";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import {
  useCompanyProfile,
  useUpdateMyCompanyContact,
  type CompanyContactPatch,
  type TestingCompanyLicense,
} from "@/hooks/use-company-profile";
import { useReReviewCure } from "@/hooks/use-re-review-cure";
import { formatDateOnly } from "@/lib/dates";
import { isValidPhone } from "@njbsoft/utils";

export const Route = createFileRoute("/_company/company/profile/")({
  component: ProfilePage,
});

// Every field a claimed company owns (CompanyContactPatch): the three identity
// fields, the seven contact/mailing fields, the license list and the insurance
// policy number / amount. The form state mirrors the scalars as non-null
// strings so the inputs are controlled; the amount is held as the input's
// string until submit converts it.
interface ContactForm {
  name: string;
  insurance_carrier: string;
  insurance_expires_at: string;
  contact_email: string;
  contact_phone: string;
  mailing_address_line1: string;
  mailing_address_line2: string;
  mailing_city: string;
  mailing_state: string;
  mailing_postal_code: string;
  licenses: TestingCompanyLicense[];
  insurance_policy_number: string;
  insurance_amount: string;
}

/**
 * The nullable scalars, every one of them Pattern A three-state on the wire:
 * unchanged is OMITTED (the server preserves it), emptied is an explicit null
 * (the server clears it). `name` is not among them — it is NOT NULL at the
 * column and non-nullable on the wire.
 */
type NullableTextField =
  | "insurance_carrier"
  | "insurance_expires_at"
  | "contact_email"
  | "contact_phone"
  | "mailing_address_line1"
  | "mailing_address_line2"
  | "mailing_city"
  | "mailing_state"
  | "mailing_postal_code"
  | "insurance_policy_number";

const NULLABLE_TEXT_FIELDS: NullableTextField[] = [
  "insurance_carrier",
  "insurance_expires_at",
  "contact_email",
  "contact_phone",
  "mailing_address_line1",
  "mailing_address_line2",
  "mailing_city",
  "mailing_state",
  "mailing_postal_code",
  "insurance_policy_number",
];

/** `undefined` to omit the field, otherwise the value the PATCH carries. */
function nullableChange(next: string, seeded: string): string | null | undefined {
  const value = next.trim();
  if (value === seeded.trim()) return undefined;
  return value.length > 0 ? value : null;
}

/**
 * Row-by-row equality on the submitted license list. A present `licenses`
 * array REPLACES the whole list server-side, so the array is sent only when it
 * actually changed; row order is part of the value, and the four wire fields
 * are compared with both spellings of "absent" collapsed.
 */
function licensesEqual(next: TestingCompanyLicense[], seeded: TestingCompanyLicense[]): boolean {
  if (next.length !== seeded.length) return false;
  return next.every((row, index) => {
    const other = seeded[index];
    return (
      other !== undefined &&
      row.license_type === other.license_type &&
      row.license_number.trim() === other.license_number.trim() &&
      (row.agency ?? null) === (other.agency ?? null) &&
      (row.expires_at ?? null) === (other.expires_at ?? null)
    );
  });
}

/**
 * The PATCH body from the CHANGED fields only. go-patterns 1's three-state
 * discipline reaches the client here: a field the admin did not touch is
 * absent from the body, never sent as null, so a concurrent edit to another
 * field is preserved instead of being cleared by this save.
 *
 * `name` is sent only when it changed to a non-empty value; the submit guard
 * is what refuses a cleared one, because the wire cannot carry an unnamed
 * company.
 */
export function buildCompanyContactPatch(
  form: ContactForm,
  seeded: ContactForm,
): CompanyContactPatch {
  const body: CompanyContactPatch = {};

  const name = form.name.trim();
  if (name.length > 0 && name !== seeded.name.trim()) body.name = name;

  for (const field of NULLABLE_TEXT_FIELDS) {
    const change = nullableChange(form[field], seeded[field]);
    if (change !== undefined) body[field] = change;
  }

  const amount = form.insurance_amount.trim();
  if (amount !== seeded.insurance_amount.trim()) {
    body.insurance_amount = amount === "" ? null : Number(amount);
  }

  if (!licensesEqual(form.licenses, seeded.licenses)) {
    body.licenses = form.licenses.map((l) => ({ ...l, license_number: l.license_number.trim() }));
  }

  return body;
}

/**
 * Save is enabled only once something differs from what the server holds. A
 * press on an untouched form would PATCH `{}`: a write that cannot change
 * anything, yet still toasts success and reads to the admin as though an edit
 * they never made had landed.
 *
 * The verdict IS the body — one derivation, so the button and the wire cannot
 * disagree — with one deliberate exception: a CLEARED name counts as dirty even
 * though the body refuses to carry it. The submit guard is what tells an admin
 * a company cannot be unnamed, and a disabled button would take that sentence
 * away and leave a greyed-out Save with no reason beside it.
 */
export function isCompanyProfileDirty(form: ContactForm, seed: ContactForm): boolean {
  if (form.name.trim().length === 0 && seed.name.trim().length > 0) return true;
  return Object.keys(buildCompanyContactPatch(form, seed)).length > 0;
}

function ProfilePage() {
  const { t } = useTranslation("company");
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const profileQuery = useCompanyProfile(companyId);
  const { success, apiError } = useToast();
  const saveMutation = useUpdateMyCompanyContact(companyId);
  // The utilities read rides the same react-query key the utilities page
  // reads, so this derivation adds no request — it only tells the form
  // whether the next save owes a cure statement.
  const cure = useReReviewCure(companyId);
  const [form, setForm] = useState<ContactForm | null>(null);
  const [whatsDifferent, setWhatsDifferent] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const [statementError, setStatementError] = useState("");

  // What the server holds, as form values. The PATCH body is the difference
  // between this and the edited form, so the seed is kept rather than merely
  // copied into state.
  const seed = useMemo<ContactForm | null>(() => {
    const c = profileQuery.data;
    if (!c) return null;
    return {
      name: c.name,
      insurance_carrier: c.insurance_carrier ?? "",
      insurance_expires_at: c.insurance_expires_at ?? "",
      contact_email: c.contact_email ?? "",
      contact_phone: c.contact_phone ?? "",
      mailing_address_line1: c.mailing_address_line1 ?? "",
      mailing_address_line2: c.mailing_address_line2 ?? "",
      mailing_city: c.mailing_city ?? "",
      mailing_state: c.mailing_state ?? "",
      mailing_postal_code: c.mailing_postal_code ?? "",
      licenses: c.licenses ?? [],
      insurance_policy_number: c.insurance_policy_number ?? "",
      insurance_amount: typeof c.insurance_amount === "number" ? String(c.insurance_amount) : "",
    };
  }, [profileQuery.data]);

  // Seed the editable form from the loaded profile; re-seed on company switch.
  useEffect(() => {
    if (seed) setForm(seed);
  }, [seed]);

  const company = profileQuery.data;
  if (profileQuery.isLoading || !company || !form || !seed) return <PageSkeleton />;

  const set = (key: Exclude<keyof ContactForm, "licenses">) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => (prev ? { ...prev, [key]: e.target.value } : prev));

  const dirty = isCompanyProfileDirty(form, seed);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form) return;
    // A company cannot be unnamed: the column is NOT NULL, so a cleared box is
    // refused here rather than silently omitted from the body.
    if (form.name.trim().length === 0) {
      setNameError(`${t("profile.fields.name")}: ${t("common:validation.required")}`);
      return;
    }
    // Phone is optional; only a non-empty malformed value blocks the save.
    if (!isValidPhone(form.contact_phone)) {
      setPhoneError(t("common:validation.invalidPhone"));
      return;
    }
    // While a curable re-review rejection stands, EVERY contact save owes the
    // cure statement — the wire refuses the whole PATCH without one (422
    // `whats_different_required`, a rollback, not a partial write). The guard
    // sits before the empty-body early-out so a statement-less save with real
    // field changes is refused here rather than after a wasted round trip.
    const statement = whatsDifferent.trim();
    if (cure.cureRequired && statement === "") {
      setStatementError(t("profile.cure.statementRequired"));
      return;
    }
    const body = buildCompanyContactPatch(form, seed);
    // Enter inside any text box submits the form even while Save is disabled,
    // so the refusal lives on the SUBMIT as well as on the button.
    if (Object.keys(body).length === 0) return;
    if (body.licenses?.some((l) => l.license_number.length === 0)) {
      setLicenseError(`${t("profile.licenses.number")}: ${t("common:validation.required")}`);
      return;
    }
    // The statement rides only a cure-state save and is never part of the
    // dirty derivation — a statement alone changes nothing, the write does.
    if (cure.cureRequired) body.whats_different = statement;
    saveMutation.mutate(body, {
      onSuccess: () => success("company:profile.saveSuccess"),
      onError: (err: unknown) => {
        const code = (err as { code?: string }).code;
        if (code === "testing_company_licenses_duplicate") {
          setLicenseError(t("profile.errors.licensesDuplicate"));
          return;
        }
        if (code === "testing_company_licenses_too_many") {
          setLicenseError(t("profile.errors.licensesTooMany", { max: MAX_LICENSES }));
          return;
        }
        if (code === "whats_different_required") {
          setStatementError(t("profile.cure.statementRequired"));
          return;
        }
        apiError(code);
      },
    });
  };

  // The company self-manages its own name and insurance identity — the three
  // fields ride the same PATCH as the contact details behind the same Save.
  const identityCard = (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-on-surface">{t("profile.identityCard")}</h2>
      </CardHeader>
      <CardContent>
        {!isAdmin ? (
          <>
            <p className="mb-4 text-sm text-muted">{t("profile.identityReadOnly")}</p>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <IdentityField label={t("profile.fields.name")} value={company.name} />
              <IdentityField
                label={t("profile.fields.insuranceCarrier")}
                value={company.insurance_carrier ?? "—"}
              />
              <IdentityField
                label={t("profile.fields.insuranceExpires")}
                value={
                  company.insurance_expires_at ? formatDateOnly(company.insurance_expires_at) : "—"
                }
              />
            </dl>
          </>
        ) : (
          <>
            <p className="mb-4 text-sm text-muted">{t("profile.identityHint")}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label={t("profile.fields.name")} error={nameError} required>
                <input
                  type="text"
                  required
                  maxLength={200}
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => {
                    setNameError("");
                    set("name")(e);
                  }}
                />
              </FormField>
              <FormField label={t("profile.fields.insuranceCarrier")}>
                <input
                  type="text"
                  maxLength={200}
                  className={inputClass}
                  value={form.insurance_carrier}
                  onChange={set("insurance_carrier")}
                />
              </FormField>
              <FormField label={t("profile.fields.insuranceExpires")}>
                <input
                  type="date"
                  className={inputClass}
                  value={form.insurance_expires_at}
                  onChange={set("insurance_expires_at")}
                />
              </FormField>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  const licensesCard = (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-on-surface">{t("profile.licensesCard")}</h2>
      </CardHeader>
      <CardContent>
        {!isAdmin ? (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-muted">{t("profile.licensesCard")}</dt>
              <dd className="mt-1 text-sm text-on-surface">
                {company.licenses.length === 0 ? (
                  t("profile.licenses.empty")
                ) : (
                  <ul className="space-y-1">
                    {company.licenses.map((l, i) => (
                      <li key={`${l.license_type}-${l.license_number}-${i}`}>
                        <span className="font-medium">
                          {t(`profile.licenses.types.${l.license_type}`)}
                        </span>{" "}
                        {l.license_number}
                        {l.agency ? <span className="text-muted"> · {l.agency}</span> : null}
                        {l.expires_at ? (
                          <span className="text-muted"> · {formatDateOnly(l.expires_at)}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
            <IdentityField
              label={t("profile.insuranceFields.policyNumber")}
              value={company.insurance_policy_number ?? "—"}
            />
            <IdentityField
              label={t("profile.insuranceFields.amount")}
              value={
                typeof company.insurance_amount === "number"
                  ? company.insurance_amount.toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                    })
                  : "—"
              }
            />
          </dl>
        ) : (
          <>
            <p className="mb-4 text-sm text-muted">{t("profile.licensesHint")}</p>
            <div className="space-y-4">
              <LicenseListEditor
                value={form.licenses}
                onChange={(licenses) => {
                  setLicenseError("");
                  setForm((prev) => (prev ? { ...prev, licenses } : prev));
                }}
              />
              {licenseError && <p className="text-xs text-danger">{licenseError}</p>}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label={t("profile.insuranceFields.policyNumber")}>
                  <input
                    type="text"
                    maxLength={100}
                    className={inputClass}
                    value={form.insurance_policy_number}
                    onChange={set("insurance_policy_number")}
                  />
                </FormField>
                <FormField label={t("profile.insuranceFields.amount")}>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    className={inputClass}
                    value={form.insurance_amount}
                    onChange={set("insurance_amount")}
                  />
                </FormField>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  const contactCard = (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-on-surface">{t("profile.contactCard")}</h2>
      </CardHeader>
      <CardContent>
        {/* Contact edits are an admin verb — a member reads the same details
            back as plain values, never as inputs behind a Save that 403s. */}
        {!isAdmin ? (
          <>
            <p className="mb-4 text-sm text-muted">{t("profile.contactReadOnly")}</p>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <IdentityField
                label={t("profile.contactFields.contactEmail")}
                value={company.contact_email ?? "—"}
              />
              <IdentityField
                label={t("profile.contactFields.contactPhone")}
                value={company.contact_phone ?? "—"}
              />
              <IdentityField
                label={t("profile.contactFields.addressLine1")}
                value={company.mailing_address_line1 ?? "—"}
              />
              <IdentityField
                label={t("profile.contactFields.addressLine2")}
                value={company.mailing_address_line2 ?? "—"}
              />
              <IdentityField
                label={t("profile.contactFields.city")}
                value={company.mailing_city ?? "—"}
              />
              <IdentityField
                label={t("profile.contactFields.state")}
                value={company.mailing_state ?? "—"}
              />
              <IdentityField
                label={t("profile.contactFields.postalCode")}
                value={company.mailing_postal_code ?? "—"}
              />
            </dl>
          </>
        ) : (
          <>
            <p className="mb-4 text-sm text-muted">{t("profile.contactHint")}</p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label={t("profile.contactFields.contactEmail")}>
                  <input
                    type="email"
                    className={inputClass}
                    value={form.contact_email}
                    onChange={set("contact_email")}
                  />
                </FormField>
                <FormField label={t("profile.contactFields.contactPhone")} error={phoneError}>
                  <input
                    type="tel"
                    className={inputClass}
                    value={form.contact_phone}
                    onChange={(e) => {
                      setPhoneError("");
                      set("contact_phone")(e);
                    }}
                  />
                </FormField>
                <FormField label={t("profile.contactFields.addressLine1")}>
                  <input
                    className={inputClass}
                    value={form.mailing_address_line1}
                    onChange={set("mailing_address_line1")}
                  />
                </FormField>
                <FormField label={t("profile.contactFields.addressLine2")}>
                  <input
                    className={inputClass}
                    value={form.mailing_address_line2}
                    onChange={set("mailing_address_line2")}
                  />
                </FormField>
                <FormField label={t("profile.contactFields.city")}>
                  <input
                    className={inputClass}
                    value={form.mailing_city}
                    onChange={set("mailing_city")}
                  />
                </FormField>
                <FormField label={t("profile.contactFields.state")}>
                  <input
                    className={inputClass}
                    value={form.mailing_state}
                    onChange={set("mailing_state")}
                  />
                </FormField>
                <FormField label={t("profile.contactFields.postalCode")}>
                  <input
                    className={inputClass}
                    value={form.mailing_postal_code}
                    onChange={set("mailing_postal_code")}
                  />
                </FormField>
              </div>
              {cure.cureRequired && (
                <FormField
                  label={t("profile.cure.statementLabel")}
                  hint={t("profile.cure.statementHint")}
                  required
                  error={statementError || undefined}
                >
                  <textarea
                    rows={3}
                    maxLength={2000}
                    aria-label={t("profile.cure.statementLabel")}
                    className={inputClass}
                    value={whatsDifferent}
                    onChange={(e) => {
                      setStatementError("");
                      setWhatsDifferent(e.target.value);
                    }}
                  />
                </FormField>
              )}
              <div className="flex justify-end">
                <Button type="submit" disabled={saveMutation.isPending || !dirty}>
                  {t("profile.save")}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 p-8">
      <PageHeader title={t("profile.title")} subtitle={company.name} />

      {/* The §4 cure notice: a standing curable re-review rejection makes the
          next save the resubmission — this banner says so where the admin is
          about to edit, and the statement field below collects what the wire
          will demand. Members see no banner: the cure write is an admin verb
          and the utilities page already renders the decision itself. */}
      {isAdmin && cure.cureRequired && (
        <div
          role="alert"
          className="rounded-lg border border-warning/50 bg-warning/10 p-4 text-sm text-on-surface"
        >
          <p className="font-semibold">{t("profile.cure.bannerTitle")}</p>
          <p className="mt-1 text-muted">
            {t("profile.cure.bannerBody", { orgNames: cure.orgNames.join(", ") })}
          </p>
        </div>
      )}

      {/* The identity, the licenses and the contact details are one PATCH
          behind one Save, so the admin arm wraps all three cards in a single
          form. The logo and evidence cards stay OUTSIDE it: each upload is its
          own multipart POST, and a button inside that form would submit the
          contact save instead. */}
      {isAdmin ? (
        <form className="space-y-6" onSubmit={handleSubmit}>
          {identityCard}
          {licensesCard}
          {contactCard}
        </form>
      ) : (
        <>
          {identityCard}
          {licensesCard}
          {contactCard}
        </>
      )}
      <CompanyLogoCard
        companyId={companyId}
        canManage={isAdmin}
        hasLogo={profileQuery.data?.has_logo}
      />
      <CompanyEvidenceCard companyId={companyId} canUpload={isAdmin} />
    </div>
  );
}

function IdentityField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-sm font-medium text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-on-surface">{value}</dd>
    </div>
  );
}
